import type { ReactNode } from 'react';

import { Card } from '@pesto/ui/components/Card';
import { Typography } from '@pesto/ui/components/Typography';
import { cn } from '@pesto/utils';
import { ChevronRight, CreditCard, User } from 'lucide-react';

import type { CardOwner, CardViewModel } from '../types/Cards.types';

import { deriveCardKind, isCurrentCard } from '../utils/deriveCardKind';
import { CardStatusBadge } from './CardStatusBadge';
import { CardVisual } from './CardVisual';

interface UserCardsPanelProps {
  cards: CardViewModel[];
  onCardClick?: (card: CardViewModel) => void;
  onUserClick?: (userId: string) => void;
  renderActions?: (card: CardViewModel) => ReactNode;
  emptyState?: ReactNode;
}

const sortByOpenedDesc = (a: CardViewModel, b: CardViewModel): number => {
  const ad = a.insertedAt ? new Date(a.insertedAt).getTime() : 0;
  const bd = b.insertedAt ? new Date(b.insertedAt).getTime() : 0;

  return bd - ad;
};

export const UserCardsPanel = ({ cards, onCardClick, onUserClick, renderActions, emptyState }: UserCardsPanelProps) => {
  if (cards.length === 0) {
    return emptyState ? <>{emptyState}</> : null;
  }

  const current = cards.filter((c) => isCurrentCard(deriveCardKind(c))).sort(sortByOpenedDesc);
  const previous = cards.filter((c) => !isCurrentCard(deriveCardKind(c))).sort(sortByOpenedDesc);

  return (
    <div className="flex flex-col gap-8">
      {current.length > 0 && (
        <Section title="Current">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {current.map((card) => (
              <CurrentCardTile
                key={card.id}
                card={card}
                onClick={onCardClick ? () => onCardClick(card) : undefined}
                onUserClick={onUserClick}
                actions={renderActions?.(card)}
              />
            ))}
          </div>
        </Section>
      )}

      {previous.length > 0 && (
        <Section title="Previous">
          <Card>
            <ul className="divide-border/60 divide-y">
              {previous.map((card) => (
                <PreviousCardRow
                  key={card.id}
                  card={card}
                  onClick={onCardClick ? () => onCardClick(card) : undefined}
                  onUserClick={onUserClick}
                  actions={renderActions?.(card)}
                />
              ))}
            </ul>
          </Card>
        </Section>
      )}
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-3">
    <Typography variant="caption" className="text-muted-foreground uppercase tracking-wide">
      {title}
    </Typography>
    {children}
  </section>
);

const CardOwnerLink = ({ owner, onUserClick }: { owner: CardOwner; onUserClick?: (userId: string) => void }) => {
  const label = owner.name ?? owner.id;

  const content = (
    <>
      <User className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </>
  );

  const base = 'text-muted-foreground inline-flex max-w-full items-center gap-1 text-xs';

  if (!onUserClick) {
    return <span className={base}>{content}</span>;
  }

  return (
    <button
      type="button"
      onClick={() => onUserClick(owner.id)}
      className={cn(base, 'hover:text-foreground hover:underline')}
    >
      {content}
    </button>
  );
};

interface TileProps {
  card: CardViewModel;
  onClick?: () => void;
  onUserClick?: (userId: string) => void;
  actions?: ReactNode;
}

const CurrentCardTile = ({ card, onClick, onUserClick, actions }: TileProps) => {
  const interactive = Boolean(onClick);

  return (
    <Card className="flex flex-col gap-4 p-4">
      <button
        type="button"
        onClick={onClick}
        disabled={!interactive}
        className={cn(
          'group flex w-full justify-center',
          interactive && 'transition-transform hover:scale-[1.01]',
          !interactive && 'cursor-default',
        )}
      >
        <CardVisual card={card} />
      </button>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <Typography variant="body" className="truncate font-medium">
            {card.referenceId ?? card.id}
          </Typography>
          {card.lastFour && (
            <Typography variant="caption" className="text-muted-foreground font-mono">
              •••• {card.lastFour}
            </Typography>
          )}
          {card.user && <CardOwnerLink owner={card.user} onUserClick={onUserClick} />}
        </div>
        {actions}
      </div>
    </Card>
  );
};

const PreviousCardRow = ({ card, onClick, onUserClick, actions }: TileProps) => {
  const interactive = Boolean(onClick);

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <button
        type="button"
        onClick={onClick}
        disabled={!interactive}
        className={cn(
          'group flex flex-1 items-center gap-4 text-left',
          interactive && 'hover:opacity-80',
          !interactive && 'cursor-default',
        )}
      >
        <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-md">
          <CreditCard className="text-muted-foreground h-5 w-5" />
        </div>
        <div className="flex-1 space-y-0.5">
          <div className="flex items-center gap-2">
            <Typography variant="body" className="truncate font-medium">
              {card.referenceId ?? card.id}
            </Typography>
            {card.lastFour && (
              <Typography variant="caption" className="text-muted-foreground font-mono">
                •••• {card.lastFour}
              </Typography>
            )}
          </div>
          <CardStatusBadge status={card.status} physicalActivatedAt={card.physicalActivatedAt} />
        </div>
        {interactive && (
          <ChevronRight className="text-muted-foreground/60 group-hover:text-muted-foreground h-4 w-4 transition-colors" />
        )}
      </button>
      {card.user && <CardOwnerLink owner={card.user} onUserClick={onUserClick} />}
      {actions && <div className="shrink-0">{actions}</div>}
    </li>
  );
};
